<script>
import { calculatePeriodMetrics } from "./reporting-mock-data";

export default {
  name: "reporting-quantity-card",
  props: {
    periodType: {
      type: String,
      default: "month",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      metrics: null,
    };
  },
  computed: {
    quantity() {
      return this.metrics?.current?.quantity || 0;
    },
    previousQuantity() {
      return this.metrics?.previous?.quantity || 0;
    },
    quantityDelta() {
      return this.metrics?.delta?.quantity || 0;
    },
    quantityPercentChange() {
      return this.metrics?.delta?.quantityPercent || 0;
    },
    ytdQuantity() {
      return this.metrics?.ytd?.currentQuantity || 0;
    },
    ytdQuantityChange() {
      return this.metrics?.ytd?.quantityChange || 0;
    },
    isPositiveTrend() {
      return this.quantityPercentChange >= 0;
    },
    isYtdPositive() {
      return this.ytdQuantityChange >= 0;
    },
    periodLabel() {
      const labels = {
        week: "last week",
        month: "last month",
        year: "last year",
      };
      return labels[this.periodType] || "last period";
    },
  },
  watch: {
    periodType: {
      handler: "loadData",
      immediate: true,
    },
  },
  methods: {
    loadData() {
      // Simulate API call with mock data
      this.metrics = calculatePeriodMetrics(this.periodType);
    },
    formatNumber(num) {
      return new Intl.NumberFormat("en-US").format(Math.round(num));
    },
  },
};
</script>

<template>
  <b-card no-body class="h-100">
    <b-card-body>
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 class="text-muted mb-1">Total Quantity</h5>
          <h2 class="mb-0 font-weight-bold">
            <b-skeleton v-if="isLoading" width="150px"></b-skeleton>
            <span v-else>{{ formatNumber(quantity) }}</span>
          </h2>
        </div>
        <div class="avatar-sm">
          <span class="avatar-title bg-soft-success rounded-3">
            <i class="bx bx-package text-success font-size-24"></i>
          </span>
        </div>
      </div>

      <div v-if="!isLoading">
        <!-- Comparison to previous period -->
        <div class="d-flex align-items-center mb-2">
          <span
            class="badge font-size-12 me-2"
            :class="
              isPositiveTrend ? 'badge-soft-success' : 'badge-soft-danger'
            "
          >
            <i
              class="bx align-middle me-1"
              :class="isPositiveTrend ? 'bx-trending-up' : 'bx-trending-down'"
            ></i>
            {{ Math.abs(quantityPercentChange).toFixed(2) }}%
          </span>
          <span class="text-muted small">vs {{ periodLabel }}</span>
        </div>

        <!-- Delta value -->
        <div class="text-muted small mb-3">
          <span :class="isPositiveTrend ? 'text-success' : 'text-danger'">
            {{ isPositiveTrend ? "+" : "" }}{{ formatNumber(quantityDelta) }}
          </span>
          units from previous period
        </div>

        <!-- YTD Comparison -->
        <div class="border-top pt-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <small class="text-muted d-block">YTD Quantity</small>
              <strong>{{ formatNumber(ytdQuantity) }}</strong>
            </div>
            <div class="text-end">
              <span
                class="badge font-size-11"
                :class="
                  isYtdPositive ? 'badge-soft-success' : 'badge-soft-danger'
                "
              >
                <i
                  class="bx align-middle"
                  :class="
                    isYtdPositive ? 'bx-up-arrow-alt' : 'bx-down-arrow-alt'
                  "
                ></i>
                {{ Math.abs(ytdQuantityChange).toFixed(2) }}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <b-skeleton width="200px" class="mb-2"></b-skeleton>
        <b-skeleton width="180px" class="mb-3"></b-skeleton>
        <b-skeleton width="220px"></b-skeleton>
      </div>
    </b-card-body>
  </b-card>
</template>

<style scoped>
.avatar-sm {
  height: 3rem;
  width: 3rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
}

.bg-soft-success {
  background-color: rgba(52, 195, 143, 0.18) !important;
}

.rounded-3 {
  border-radius: 0.5rem !important;
}
</style>
